import { Component, ElementRef, forwardRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonDatetime, IonModal } from '@ionic/angular';
import * as moment from 'moment';
import { AlertService } from '../../services/alert.service';
import { FIFTY_FIVE_YEARS_AGO, INPUT_DATE_FORMAT } from '../../utils/constants';

type SegmentType = 'day' | 'month' | 'year';
interface DateSegment { type: SegmentType; placeholder: string; maxLen: number; ariaLabel: string; }

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    }
  ]
})
export class DateInputComponent implements OnInit, ControlValueAccessor {

  @Input() locale: string = 'en-AU';
  @Input() maxDate: string = moment().format(INPUT_DATE_FORMAT);
  @Input() minDate: string = '1900-01-01';

  @ViewChild('pickerModal') pickerModal!: IonModal;
  @ViewChild('pickerDatetime') pickerDatetime?: IonDatetime;
  @ViewChildren('segInput') segInputs!: QueryList<ElementRef<HTMLInputElement>>;

  segments: DateSegment[] = [];
  separator = '/';
  segmentValues: Record<SegmentType, string> = { day: '', month: '', year: '' };

  isDisabled = false;

  private _value = '';
  get value(): string { return this._value; }
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(private alertProvider: AlertService) {}

  ngOnInit() {
    const info = this.buildSegmentInfo(this.locale);
    this.segments = info.segments;
    this.separator = info.separator;
  }

  writeValue(value: string): void {
    this._value = value || '';
    if (value && moment(value, INPUT_DATE_FORMAT, true).isValid()) {
      const m = moment(value, INPUT_DATE_FORMAT);
      this.segmentValues = { day: m.format('DD'), month: m.format('MM'), year: m.format('YYYY') };
    } else {
      this.segmentValues = { day: '', month: '', year: '' };
    }
  }

  registerOnChange(fn: (value: string) => void): void { this._onChange = fn; }
  registerOnTouched(fn: () => void): void { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.isDisabled = isDisabled; }

  get isDateValid(): boolean {
    return moment(this._value, INPUT_DATE_FORMAT, true).isValid();
  }

  get verboseDate(): string {
    return moment(this._value, INPUT_DATE_FORMAT).format('D MMMM YYYY');
  }

  onSegmentFocus(event: Event) {
    const input = event.target as HTMLInputElement;
    setTimeout(() => {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }, 0);
  }

  onSegmentInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const seg = this.segments[index];
    let val = input.value.replace(/\D/g, '').slice(0, seg.maxLen);

    input.value = val;
    this.segmentValues[seg.type] = val;
    this.emitValue();

    if (val.length === seg.maxLen) {
      this.focusSegment(index + 1, true);
    }
  }

  onSegmentKeydown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (input.value === '' && index > 0) {
        event.preventDefault();
        this.focusSegment(index - 1, false);
      }
    } else if (event.key === '/') {
      event.preventDefault();
      const seg = this.segments[index];
      if (seg.maxLen === 2 && input.value.length === 1) {
        const padded = '0' + input.value;
        input.value = padded;
        this.segmentValues[seg.type] = padded;
        this.emitValue();
      }
      this.focusSegment(index + 1, true);
    }
  }

  onSegmentBlur(index: number) {
    const seg = this.segments[index];
    const input = this.segInputs.toArray()[index]?.nativeElement;
    if (!input) return;

    if (seg.maxLen === 2 && input.value.length === 1) {
      const padded = '0' + input.value;
      input.value = padded;
      this.segmentValues[seg.type] = padded;
      this.emitValue();
    } else if (seg.type === 'year' && input.value.length === 4) {
      const clamped = this.clampYear(input.value);
      if (clamped !== input.value) {
        input.value = clamped;
        this.segmentValues[seg.type] = clamped;
        this.emitValue();
      }
    }

    this._onTouched();
  }

  openDobPicker() {
    if (this.pickerDatetime) {
      this.pickerDatetime.value = this.isDateValid ? this._value : FIFTY_FIVE_YEARS_AGO;
    }
    this.pickerModal.present();
  }

  onPickerChange(event: CustomEvent) {
    const value = event.detail.value as string;
    this._value = value;
    this._onChange(value);
    this._onTouched();
    const m = moment(value, INPUT_DATE_FORMAT);
    this.segmentValues = { day: m.format('DD'), month: m.format('MM'), year: m.format('YYYY') };
  }

  async confirmPicker() {
    await this.pickerDatetime?.confirm(false);
    this.pickerModal.dismiss();
  }

  cancelPicker() {
    if (!this.isDateValid) {
      this._onTouched();
      this.pickerModal.dismiss();
      return;
    }
    this.alertProvider.confirm(
      'Close date picker?',
      'Are you sure you want to close without updating the date?',
      'Close',
      () => {
        this._onTouched();
        this.pickerModal.dismiss();
      }
    );
  }

  private clampYear(yearStr: string): string {
    const year = parseInt(yearStr, 10);
    const minYear = parseInt(this.minDate.substring(0, 4), 10);
    const maxYear = parseInt(this.maxDate.substring(0, 4), 10);
    if (year < minYear) return String(minYear);
    if (year > maxYear) return String(maxYear);
    return yearStr;
  }

  private focusSegment(index: number, selectAll: boolean) {
    const target = this.segInputs.toArray()[index]?.nativeElement;
    if (!target) return;
    target.focus();
    if (selectAll) target.select();
  }

  private emitValue() {
    const { day, month, year } = this.segmentValues;
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      const iso = `${year}-${month}-${day}`;
      const parsed = moment(iso, INPUT_DATE_FORMAT, true);
      const inRange = parsed.isValid()
        && (!this.minDate || iso >= this.minDate)
        && (!this.maxDate || iso <= this.maxDate);
      this._value = inRange ? iso : `${day}/${month}/${year}`;
    } else {
      this._value = day || month || year ? `${day}/${month}/${year}` : '';
    }
    this._onChange(this._value);
  }

  private buildSegmentInfo(locale: string): { segments: DateSegment[], separator: string } {
    const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
      .formatToParts(new Date(2000, 0, 15));

    const segments: DateSegment[] = [];
    let separator = '/';

    for (const part of parts) {
      if (part.type === 'day')        segments.push({ type: 'day',   placeholder: 'DD',   maxLen: 2, ariaLabel: 'Day' });
      else if (part.type === 'month') segments.push({ type: 'month', placeholder: 'MM',   maxLen: 2, ariaLabel: 'Month' });
      else if (part.type === 'year')  segments.push({ type: 'year',  placeholder: 'YYYY', maxLen: 4, ariaLabel: 'Year' });
      else if (part.type === 'literal' && segments.length > 0 && segments.length < 3) {
        separator = part.value.trim();
      }
    }

    return { segments, separator };
  }
}
